import type { FC, ReactNode } from "react";
import { ChatActionBar } from "@/ui/Chat/ChatActionBar";
import type { AnnieFeedbackRequest, AnnieFeedbackState } from "@/utils/annieStream";
import { AnnieFeedbackDetailsEditor } from "./AnnieFeedbackDetailsEditor";
import { AnnieFeedbackRatingButtons } from "./AnnieFeedbackRatingButtons";
import { useAnnieFeedbackEditor } from "./useAnnieFeedbackEditor";

type Props = {
	conversationId: number;
	feedback: AnnieFeedbackState;
	isSaving: boolean;
	onSave: (replacement: AnnieFeedbackRequest) => Promise<boolean>;
	/** Answer actions that share the bar with the ratings (copy, regenerate) — rendered ahead of them. */
	actions?: ReactNode;
};

/** The answer's action bar — copy/regenerate plus inline, editable rating controls. */
export const AnnieAnswerFeedback: FC<Props> = ({ conversationId, feedback, isSaving, onSave, actions }) => {
	const editor = useAnnieFeedbackEditor(feedback, isSaving, onSave);
	// The bar is hover-revealed by default, but pins open once the answer carries feedback state the user
	// should see at rest — rated, saving, or with the details editor open beneath it.
	const isPinnedVisible = editor.isOpen || isSaving || feedback.rating !== null;

	return (
		<>
			<ChatActionBar alwaysVisible={isPinnedVisible} dataTestId={`annie-answer-feedback-${conversationId}`}>
				{actions}
				<AnnieFeedbackRatingButtons
					conversationId={conversationId}
					rating={feedback.rating}
					isSaving={isSaving}
					isSavingDetails={editor.isSavingDetails}
					onHelpful={editor.selectHelpful}
					onUnhelpful={editor.selectUnhelpful}
				/>
			</ChatActionBar>
			{editor.isOpen && <AnnieFeedbackDetailsEditor conversationId={conversationId} editor={editor} />}
		</>
	);
};
